import React from "react";

import { I18n } from "react-redux-i18n";

import { getTestAttributes } from "@common/utils/tests";
import { FormField } from "@common/components/form-field";
import { Button } from "@common/components/Button";
import { Flex } from "@common/components/Flex";
import { Ellipsis } from "@common/components/Ellipsis";
import {
  FieldSelector,
  FieldSelectorGetFieldParams,
} from "@common/components/FieldSelector";
import { PedigreeSettingsFormatManagementFieldDelimiters } from "@common/dto/pedigree/format-field-delimiters";
import Dropdown from "@common/containers/fields/Dropdown";

import { PedigreeSettingsFieldsManagementProps } from "./fields-management.types";
import { useController } from "./fields-management.controller";

import "./fields-management.styles.scss";
import { ErrorText } from "@common/components/error-text";

export const FieldsManagement: React.FC<
  PedigreeSettingsFieldsManagementProps
> = (props) => {
  const { config, getFields, getFieldsFolders, errors } = props;

  const {
    selectedFields,
    fieldChooserOpened,
    delimiterOptions,
    openFieldChooser,
    closeFieldChooser,
    handleDelimiterOptionSelect,
    handleFieldSelect,
    handleFieldRemove,
    handleSelectedFieldChange,
    selectedField,
  } = useController(props);

  const hasDelimiterError = (index: number, fieldId: number) => {
    if (index === selectedFields.length - 1) return false;
    if (!fieldId) return false;
    const hasGeneralError = errors?.properties?.pednameFields?.[0]?.text;
    return hasGeneralError && !selectedFields[index]?.delimiter;
  };

  return (
    <>
      <div>
        {fieldChooserOpened && (
          <FieldSelector
            selectedFolder={selectedField?.folder}
            selectedFields={
              selectedField?.field ? [selectedField.field] : undefined
            }
            withSearch
            getFolders={getFieldsFolders}
            getFields={({
              searchKeyword,
              context,
              ...restParams
            }: FieldSelectorGetFieldParams) =>
              getFields({
                ...restParams,
                searchQuery: searchKeyword,
                contextId: context,
                includeTables: true,
              })
            }
            onChange={handleFieldSelect}
            close={closeFieldChooser}
            closeOnChange={false}
          />
        )}
        <div>
          <FormField
            inputWidth="100%"
            labelWidth={100}
            label={config.addFieldsLabel}
            {...getTestAttributes({ id: "lbl-create-format-wnd-name" })}
          >
            <Flex
              className="PedigreeSettingsFieldsManagement__fields"
              wrap="wrap"
            >
              {selectedFields.map((selectedItem, index) => (
                <div>
                  <Flex
                    key={selectedItem.internalUniqId}
                    alignItems="center"
                    className="PedigreeSettingsFieldsManagement__fields__field"
                  >
                    <Button
                      {...getTestAttributes({ id: "btn-remove-field" })}
                      onClick={() => handleFieldRemove(selectedItem)}
                      variant="text"
                      className="PedigreeSettingsFieldsManagement__fields__field__remove"
                    >
                      <span className="fa fa-remove"></span>
                    </Button>
                    <div
                      {...getTestAttributes({ id: "btn-field-chooser" })}
                      className="PedigreeSettingsFieldsManagement__trigger"
                      onClick={() => handleSelectedFieldChange(selectedItem)}
                    >
                      <Ellipsis
                        text={selectedItem.fieldName || ""}
                        {...getTestAttributes({
                          id: "tx-create-format-wnd-folder",
                        })}
                        maxWidth={150}
                      />
                      <img src="gif/df_warehouse.gif" alt="warehouse icon"/>
                    </div>
                    <Dropdown
                      {...getTestAttributes({ id: "lbx-delimiters" })}
                      placeholder={I18n.t("Select Delimiter")}
                      selectedValue={selectedItem.delimiter}
                      searchable={false}
                      withEmptyOption
                      className="PedigreeSettingsFieldsManagement__fields__delimiter__selector"
                      options={delimiterOptions}
                      onValueChanged={(
                        selectedValue: PedigreeSettingsFormatManagementFieldDelimiters
                      ) =>
                        handleDelimiterOptionSelect(selectedItem, selectedValue)
                      }
                    />
                  </Flex>
                  {hasDelimiterError(index, selectedItem.fieldId) && (
                    <ErrorText withTopGap className="field-specific-error">
                      {errors?.properties?.pednameFields[0]?.text}
                    </ErrorText>
                  )}
                </div>
              ))}
              <Button
                {...getTestAttributes({ id: "btn-add-field" })}
                onClick={openFieldChooser}
                variant="text"
                disabled={!selectedFields[0]?.fieldId}
                className="PedigreeSettingsFieldsManagement__add-field"
              >
                <span className="fa fa-plus"></span>
              </Button>
            </Flex>
          </FormField>
        </div>
      </div>
    </>
  );
};
